class WeightCalculator extends HTMLElement {
  minRecommendedWeight = 5;
  formula = 'standard';

  connectedCallback() {
    this.input = this.querySelector('.weight-calculator__input');
    this.rangeOutput = this.querySelector('[data-range-value]');
    this.recommendedOutput = this.querySelector('[data-recommended-value]');

    if (!this.input) return;

    this.formula = this.dataset.formula || 'standard';
    this.minRecommendedWeight = Number(this.dataset.minRecommendedWeight) || (this.formula === 'kids' ? 2 : 5);
    this.handleRangeInput = this.handleRangeInput.bind(this);
    this.handleRangeCommit = this.handleRangeCommit.bind(this);
    this.lastCommittedRecommended = Number(this.recommendedOutput?.getAttribute('data-recommended-value'));
    this.preserveBodyWeightOnNextUpdate = null;
    this.variantWeights = this.getVariantWeights();

    this.init();
  }

  init() {
    this.input.addEventListener('input', this.handleRangeInput);
    this.input.addEventListener('change', this.handleRangeCommit);
  }

  disconnectedCallback() {
    this.input?.removeEventListener('input', this.handleRangeInput);
    this.input?.removeEventListener('change', this.handleRangeCommit);
  }

  getVariantSelects() {
    return this.closest('product-info')?.querySelector('variant-selects');
  }

  getVariantWeights() {
    const raw = this.dataset.variantWeights;
    if (!raw) return [];

    try {
      const weights = JSON.parse(raw)
        .map((weight) => Number(weight))
        .filter((weight) => !Number.isNaN(weight) && weight >= this.minRecommendedWeight);

      return [...new Set(weights)].sort((a, b) => a - b);
    } catch {
      return [];
    }
  }

  getRecommendedWeight(bodyWeight) {
    if (this.formula === 'kids') {
      if (bodyWeight <= 30) return 2;
      if (bodyWeight <= 40) return 3;
      return 4;
    }

    const calculated = Math.round(bodyWeight * 10) / 100 - 1;
    const baseWeight = Math.floor(calculated);
    const decimalWeight = Math.round((calculated - baseWeight) * 10) / 10;

    const recommended = decimalWeight >= 0.8 ? baseWeight + 1 : baseWeight;

    return Math.max(this.minRecommendedWeight, recommended);
  }

  getValidatedRecommended(calculated) {
    const minimumCalculated = Math.max(this.minRecommendedWeight, calculated);
    if (!this.variantWeights.length) return minimumCalculated;

    let result = this.variantWeights[0];
    for (const option of this.variantWeights) {
      if (option <= minimumCalculated) {
        result = option;
      } else {
        break;
      }
    }

    return result;
  }

  formatWeightKg(weight) {
    const rounded = Math.round(weight * 10) / 10;
    return Number.isInteger(rounded) ? `${rounded}` : rounded.toFixed(1);
  }

  formatBodyWeightKg(weight) {
    const max = Number(this.input.max);
    const formattedWeight = this.formatWeightKg(weight);

    return !Number.isNaN(max) && weight >= max ? `${formattedWeight} kg+` : `${formattedWeight} kg`;
  }

  getBodyWeightFromRecommended(selectedWeight) {
    const weight = Number(selectedWeight);
    if (Number.isNaN(weight) || weight <= 0) return null;
    const recommendedWeight = Math.max(this.minRecommendedWeight, weight);

    const min = Number(this.input.min);
    const max = Number(this.input.max);
    let bodyWeight;

    if (this.formula === 'kids') {
      if (recommendedWeight <= 2) {
        bodyWeight = 30;
      } else if (recommendedWeight <= 3) {
        bodyWeight = 40;
      } else {
        bodyWeight = 50;
      }
    } else {
      bodyWeight = (recommendedWeight + 1) * 10;
    }

    if (bodyWeight < min) bodyWeight = min;
    if (bodyWeight > max) bodyWeight = max;

    return { weight: recommendedWeight, bodyWeight };
  }

  applyRecommendedDisplay(recommended, options = {}) {
    if (!this.recommendedOutput) return;

    const { commit = true } = options;

    this.recommendedOutput.setAttribute('data-recommended-value', String(recommended));
    this.recommendedOutput.textContent = `${this.formatWeightKg(recommended)} kg`;
    if (commit) {
      this.lastCommittedRecommended = recommended;
    }
  }

  getRecommendedFromInput() {
    const value = Number(this.input.value);
    const calculated = this.getRecommendedWeight(value);

    return this.getValidatedRecommended(calculated);
  }

  handleRangeInput() {
    const recommended = this.getRecommendedFromInput();

    this.updateFill();
    this.applyRecommendedDisplay(recommended, { commit: false });
  }

  handleRangeCommit() {
    const value = Number(this.input.value);
    const recommended = this.getRecommendedFromInput();

    if (!Number.isNaN(this.lastCommittedRecommended) && this.lastCommittedRecommended === recommended) {
      return;
    }

    const variantSelects = this.getVariantSelects();

    if (variantSelects?.selectOptionValueByWeight) {
      this.preserveBodyWeightOnNextUpdate = value;

      if (variantSelects.selectOptionValueByWeight(recommended)) {
        this.lastCommittedRecommended = recommended;
        return;
      }

      this.preserveBodyWeightOnNextUpdate = null;
    }

    this.applyRecommendedDisplay(recommended);
  }

  update(selectedWeight) {
    const parsed = this.getBodyWeightFromRecommended(selectedWeight);
    if (!parsed) return;

    const { weight, bodyWeight } = parsed;
    const preserveBodyWeight = this.preserveBodyWeightOnNextUpdate;
    this.preserveBodyWeightOnNextUpdate = null;

    const currentWeight = Number(this.recommendedOutput?.getAttribute('data-recommended-value'));

    if (preserveBodyWeight != null && !Number.isNaN(preserveBodyWeight)) {
      this.applyRecommendedDisplay(weight);
      this.updateFill();
      return;
    }

    if (!Number.isNaN(currentWeight) && currentWeight === weight) return;

    this.input.value = bodyWeight;
    this.applyRecommendedDisplay(weight);
    this.updateFill();
  }

  updateFill() {
    const min = Number(this.input.min);
    const max = Number(this.input.max);
    const value = Number(this.input.value);
    const span = max - min;
    const percent = span > 0 ? ((value - min) / span) * 100 : 0;

    this.input.style.setProperty('--fill-percent', `${percent}%`);

    if (this.rangeOutput) {
      this.rangeOutput.textContent = this.formatBodyWeightKg(value);
    }
  }
}

if (!customElements.get('weight-calculator')) {
  customElements.define('weight-calculator', WeightCalculator);
}
